import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Ticket, LogOut, User } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";

/**
 * UserMenu — Login button when anonymous, avatar dropdown when signed in.
 */
export default function UserMenu({ compact = false }) {
  const { user, login, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  if (!user) {
    return (
      <Button
        onClick={login}
        data-testid="login-btn"
        variant="outline"
        className={`rounded-full border-[#1F4D3A] text-[#1F4D3A] hover:bg-[#1F4D3A] hover:text-white ${compact ? "w-full" : "px-4"}`}
      >
        <User className="w-4 h-4 mr-2" /> Log in
      </Button>
    );
  }

  const initials = (user.name || user.email || "?").split(" ").slice(0, 2).map((s) => s[0]?.toUpperCase()).join("");

  return (
    <div ref={ref} className={`relative ${compact ? "w-full" : ""}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="user-menu-trigger"
        className={`flex items-center gap-2 rounded-full ${compact ? "w-full px-4 py-2 border border-[#E5DDCB] justify-start" : "pl-1 pr-3 py-1 hover:bg-[#EFE7D6]"} transition-colors`}
      >
        {user.picture ? (
          <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full object-cover" />
        ) : (
          <div className="w-8 h-8 rounded-full bg-[#1F4D3A] text-white text-xs font-semibold flex items-center justify-center">{initials}</div>
        )}
        <span className="text-sm font-medium text-[#1F4D3A] truncate max-w-[110px]">{user.name?.split(" ")[0] || "Account"}</span>
      </button>
      {open && (
        <div
          data-testid="user-menu-dropdown"
          className={`${compact ? "static mt-2" : "absolute right-0 mt-2"} w-56 bg-white border border-[#E5DDCB] rounded-2xl shadow-lg overflow-hidden z-40`}
        >
          <div className="px-4 py-3 border-b border-[#E5DDCB]">
            <p className="text-sm font-semibold text-[#1F4D3A] truncate">{user.name}</p>
            <p className="text-xs text-[#6B8E5A] truncate">{user.email}</p>
          </div>
          <Link
            to="/wishlist"
            onClick={() => setOpen(false)}
            data-testid="menu-link-wishlist"
            className="flex items-center gap-3 px-4 py-3 text-sm text-[#4A3728] hover:bg-[#F5F0E8]"
          >
            <Heart className="w-4 h-4 text-[#C87A50]" /> My Wishlist
          </Link>
          <Link
            to="/my-bookings"
            onClick={() => setOpen(false)}
            data-testid="menu-link-bookings"
            className="flex items-center gap-3 px-4 py-3 text-sm text-[#4A3728] hover:bg-[#F5F0E8]"
          >
            <Ticket className="w-4 h-4 text-[#C87A50]" /> My Bookings
          </Link>
          <button
            onClick={async () => { setOpen(false); await logout(); }}
            data-testid="menu-logout-btn"
            className="w-full text-left flex items-center gap-3 px-4 py-3 text-sm text-[#4A3728] hover:bg-[#F5F0E8] border-t border-[#E5DDCB]"
          >
            <LogOut className="w-4 h-4 text-[#C87A50]" /> Log out
          </button>
        </div>
      )}
    </div>
  );
}
